import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/pro-regular-svg-icons/faCheck";
import type { DocumentElement as DocumentElementType } from "@eaco/forms-core";

import PageRichText from "./PageRichText";
import { usePageTextBlock } from "./usePageTextBlock";
import { cn } from "../lib/utils";

interface Props {
  element: DocumentElementType;
}

/**
 * An action item as a reader sees it — in a PDF, or on a page shared publicly.
 *
 * Only what the block itself carries: the title and whether it is done. The
 * assignee, due date, notes and tags live on the action item RECORD, which the
 * editor loads and a reader outside Eaco has no business seeing — so a shared
 * page shows the item by name, exactly as someone who cannot see a private
 * item does inside the app.
 */
const PageActionItemView: React.FC<Props> = ({ element }) => {
  const data = element.data as { content?: string; checked?: boolean };
  const checked = data?.checked === true;
  const { richTextProps } = usePageTextBlock(element);

  return (
    <div className="flex gap-[11px] rounded-[10px] border border-[#e8e6e1] bg-white px-[14px] py-[11px]">
      <span
        role="img"
        aria-label={checked ? "Done" : "Not done"}
        className={cn(
          "mt-[3px] flex h-[17px] w-[17px] flex-shrink-0 items-center justify-center rounded-[5px] border-[1.6px]",
          checked
            ? "border-[#2f6fdb] bg-[#2f6fdb] text-white"
            : "border-[#cbc7bf] bg-white",
        )}
      >
        {checked && <FontAwesomeIcon icon={faCheck} className="h-[9px] w-[9px]" />}
      </span>
      <div className="min-w-0 flex-1">
        <PageRichText
          {...richTextProps}
          editable={false}
          className={cn(
            "text-[14px] font-semibold text-[#33302b]",
            checked && "line-through opacity-50",
          )}
        />
      </div>
    </div>
  );
};

export default PageActionItemView;
